import { useMaster } from 'context/MasterContext';
import { format } from 'date-fns';
import { useCallback, useEffect, useState, useRef } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageModal } from 'components/modal/MessageModal';
import SimilarModal from 'views/ClientManage/SimilarModal';
import { useRestApi } from 'context/RestApiContext';
import TextComponent from 'components/Input/TextComponent';
import AutoCompleteComponent from 'components/Input/AutoComplete';
import SelectComponent from 'components/Input/SelectComponent';
import { checkRequired } from 'utils/validationUtils';
import { getAddress } from 'utils/addresUtils';
import { getAge, getAgeCategory, getBirthYearFromAge } from 'utils/ageUtils';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  ClientType,
  ProjectType,
  ProjectTypes,
  KoujiKbn1,
  KoujiKbn2,
  VisitReason,
  Areas,
  AgeCategories,
  DmKbn,
  TelRelation,
} from 'constants/constants';
import './ClientEdit.css';

const ClientEdit = (props) => {
  const restApi = useRestApi();

  // 確認ダイアログ
  const [dialog, setDialog] = useState({});

  // マスタ情報
  const master = useMaster();
  const navigate = useNavigate();

  // 画面パラメタを取得
  const location = useLocation();
  let targetClient = location?.state?.targetClient;

  // 工事情報を合わせて入力するかチェック
  const [isKoujiInput, setIsKoujiInput] = useState(true);
  // 類似のアラート表示するか
  const [isSimilar, setIsSimilar] = useState(false);

  // 類似顧客情報
  const [similarClient, setSimilarClient] = useState([]);

  // 年齢
  const [age, setAge] = useState(null);

  // 重複チェック検索条件
  const [duplicateCheckCriteria, setDuplicateCheckCriteria] = useState({
    CLIENT_TYPE: null,
    SEIKYU_NAME: null,
    LAST_NAME_1: null,
    NAME_KANA_1: null,
    POST_CODE: null,
  });

  // 類似顧客確認モーダルパラメタ
  const [similarModalParam, setSimilarModalParam] = useState({});

  // 生年月日の整合性アラート表示
  const [isBirthday, setIsBirthday] = useState(false);

  // 工事情報
  const [construction, setConstruction] = useState({
    DEPARTMENT: '',
    REPRESENTATIVE: '',
    KOUJI_MEI: '',
    IRAI_MEI: '',
    UKE_DATE: format(new Date(), 'yyyy/MM/dd'),
    UKE_TYPE: '',
    UKE_TYPE_BIKO: '',
    VISIT_REASON: 0,
    VISIT_REASON_BIKO: '',
    PROJECT_TYPE: '',
    KOUJI_KBN1: '',
    KOUJI_KBN2: '',
    UKE_TAN_CD: '',
    SALES_TAN_CD: '',
    GENBA_TAN_CD: 0,
    AGE_CATEGORY: 0,
    MEETING_SCHEDULED_DATE: null,
    CONSTRUCTION_STATUS: 1,
  });

  // 画面モード
  let mode = location?.state?.mode;
  const [client, setClient] = useState(() => {
    // targetClientが存在する場合の処理
    if (targetClient) {
      // BIRTHDAYが存在している場合
      const birthday = targetClient.BIRTHDAY ? new Date(targetClient.BIRTHDAY) : null;
      // 年齢を設定
      if (birthday) {
        setAge(getAge(targetClient.BIRTHDAY));
      }
      return {
        ...targetClient,
        // BIRTHDAYが存在すれば、年、月、日を抽出して設定
        BIRTHDAY_DAY: birthday ? birthday.getDate() : 1,
        BIRTHDAY_MONTH: birthday ? birthday.getMonth() + 1 : 1,
        BIRTHDAY_YEAR: birthday ? birthday.getFullYear().toString() : '',
      };
    }

    // targetClientが存在しない場合のデフォルト値
    return {
      CLIENT_TYPE: ClientType.USER,
      NAME_1: '',
      LAST_NAME_1: '',
      FIRST_NAME_1: '',
      NAME_KANA_1: '',
      NAME_2: '',
      LAST_NAME_2: '',
      FIRST_NAME_2: '',
      NAME_KANA_2: '',
      POST_CODE: '',
      PREF: '',
      CITY: '',
      TOWN: '',
      ADDRESS: '',
      BUILDING: '',
      SEIKYU_NAME: '',
      SEIKYU_POST_CODE: '',
      SEIKYU_PREF: '',
      SEIKYU_CITY: '',
      SEIKYU_TOWN: '',
      SEIKYU_ADDRESS: '',
      SEIKYU_BUILDING: '',
      TEL1: '',
      TEL_RELATION_1: '',
      TEL2: '',
      TEL_RELATION_2: '',
      MAIL: '',
      BIRTHDAY: null,
      BIRTHDAY_YEAR: '',
      BIRTHDAY_MONTH: 1,
      BIRTHDAY_DAY: 1,
      DM_KBN: 1,
      FAMILY_ADULT: 0,
      FAMILY_CHILD: 0,
      CARS: 0,
      BICYCLES: 0,
      HOUSE_MAKER: '',
      HIKIWATASHI_YOTEI: null,
      BIKO: '',
      FIRST_INQUIRY_DAY: format(new Date(), 'yyyy/MM/dd'),
      HIKKOSI_YOTEI: null,
      INPUT_SEIKYU_FLG: 0,
    };
  });

  // エラーフィールド
  const [errorFileds, setErrorFileds] = useState([]);

  // 編集処理
  const handleChange = (prop) => (target) => {
    const targetValue = target?.value ?? target?.target.value;

    let _client = { ...client, [prop]: targetValue };
    if (prop == 'AGE') {
      let replaceValue = targetValue.replace(/[^0-9]/g, '');
      setAge(replaceValue);
    }

    //　続柄の場合、Textを設定
    if (prop == 'TEL_RELATION_1' || prop == 'TEL_RELATION_2') {
      _client = {
        ...client,
        [prop]: target.value,
      };
    }

    // 請求先情報を分ける場合
    if (prop == 'INPUT_SEIKYU_FLG') {
      _client = { ..._client, INPUT_SEIKYU_FLG: target.target.checked ? 1 : 0 };
    }

    // 電話番号または年の場合
    if (prop == 'TEL1' || prop == 'TEL2' || prop == 'BIRTHDAY_YEAR') {
      // 数字以外を除去
      let replaceValue = targetValue.replace(/[^0-9]/g, '');
      _client = { ...client, [prop]: replaceValue };
    }

    // エラーのクリア
    setErrorFileds([...errorFileds].filter((x) => x != prop));
    setClient(_client);
  };

  // 工事情報の編集処理
  const handleOnChangeConstruction = (prop) => (target) => {
    let _construction = { ...construction, [prop]: target?.value ?? target?.target.value };
    // 区分③の場合に区分④の範囲外の場合に区分4をクリア
    if (prop == 'KOUJI_KBN1') {
      const k = KoujiKbn2.find((x) => x.key == construction.KOUJI_KBN2);
      if (k) {
        if (!k.KoujiKbn1.includes(parseInt(target?.value))) {
          _construction = { ..._construction, KOUJI_KBN2: '' };
        }
      }
    }
    // 区分①の場合、来店きっかけをクリア
    if (prop === 'PROJECT_TYPE') {
      _construction.VISIT_REASON = 0;
    }
    setConstruction(_construction);

    // エラーのクリア
    setErrorFileds([...errorFileds].filter((x) => x != prop));
  };

  // 顧客種別を修正
  const handleOnChangeClientType = (e) => {
    let clientType = e.target.value;
    setClient({ ...client, CLIENT_TYPE: clientType });
    checkDuplicate(clientType);
  };

  // 郵便番号検索
  const searchZip = (prefix) => {
    getAddress(
      master,
      restApi,
      client[prefix + 'POST_CODE'],
      (json) => {
        let result = json['results'][0];
        setClient({
          ...client,
          [prefix + 'PREF']: result['address1'],
          [prefix + 'CITY']: result['address2'],
          [prefix + 'TOWN']: result['address3'],
        });
        setErrorFileds(
          [...errorFileds].filter(
            (x) => x != prefix + 'PREF' && x != prefix + 'CITY' && x != prefix + 'TOWN'
          )
        );
      },
      setDialog
    );
  };

  // 前回の重複チェックと検索条件を比較
  const checkDuplicateOnFocusOut = (_client) => {
    if (
      _client.CLIENT_TYPE == duplicateCheckCriteria.CLIENT_TYPE &&
      _client.SEIKYU_NAME == duplicateCheckCriteria.SEIKYU_NAME &&
      _client.LAST_NAME_1 == duplicateCheckCriteria.LAST_NAME_1 &&
      _client.NAME_KANA_1 == duplicateCheckCriteria.NAME_KANA_1 &&
      _client.POST_CODE == duplicateCheckCriteria.POST_CODE
    ) {
      return false; // 同じ条件なので重複チェックしない
    }

    // 条件が違う場合、新しい条件を保存
    setDuplicateCheckCriteria({
      CLIENT_TYPE: _client.CLIENT_TYPE,
      SEIKYU_NAME: _client.SEIKYU_NAME,
      LAST_NAME_1: _client.LAST_NAME_1,
      NAME_KANA_1: _client.NAME_KANA_1,
      POST_CODE: _client.POST_CODE,
    });

    return true; // 新しい条件なので重複チェックする
  };

  // 重複チェック共通関数
  const checkSimilarClient = (_client, noLoading = true) => {
    // カナの先頭3文字を取得
    const nameKana = _client.NAME_KANA_1 ? _client.NAME_KANA_1.substring(0, 3) : '';
    const requestData = {
      ..._client,
      NAME_KANA_1: nameKana,
    };
    console.log(requestData);
    restApi.post(
      '/api/client/similar',
      requestData,
      (data) => {
        setIsSimilar(data.length > 0);
        setSimilarClient(data);
      },
      noLoading
    );
  };

  // 重複チェック
  const checkDuplicate = (_clientType) => {
    let _client = client;
    _client = { ..._client, CLIENT_TYPE: _clientType ? _clientType : client.CLIENT_TYPE };

    // 前回の重複チェックと検索条件を比較し同じなら重複チェックしない
    if (!checkDuplicateOnFocusOut(_client)) {
      return;
    }
    if (
      _client.CLIENT_TYPE == ClientType.USER &&
      (_client.LAST_NAME_1 || _client.NAME_KANA_1) &&
      _client.POST_CODE
    ) {
      // 個人の場合の重複チェック
      if (_client.POST_CODE.length == 7) {
        _client.POST_CODE =
          _client.POST_CODE.substring(0, 3) + '-' + _client.POST_CODE.substring(3, 7);
      }
      checkSimilarClient({
        ..._client,
      });
    } else if (_client.SEIKYU_NAME) {
      checkSimilarClient(_client);
    } else {
      setIsSimilar(false);
      setSimilarClient(null);
    }
  };

  // 生年月日が存在するかチェック
  const isValidDate = (dateStr) => {
    // 日付の形式が 'YYYY/MM/DD' であることを確認
    const regex = /^\d{4}\/\d{2}\/\d{2}$/;
    if (!regex.test(dateStr)) return false;

    // 正常な日付かをチェック
    const [year, month, day] = dateStr.split('/').map(Number);

    // Dateオブジェクトで日付を作成
    const date = new Date(year, month - 1, day);

    // 入力した年、月、日が正常な日付かをチェック
    return date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day;
  };

  const isValidBirthdate = () => {
    let _client = client;
    // 年月日を結合して生年月日フォーマットに
    const formattedBirthday = `${_client.BIRTHDAY_YEAR}/${String(_client.BIRTHDAY_MONTH).padStart(
      2,
      '0'
    )}/${String(_client.BIRTHDAY_DAY).padStart(2, '0')}`;
    // 生年月日の整合性チェック
    if (isValidDate(formattedBirthday)) {
      _client.BIRTHDAY = formattedBirthday;
      setAge(getAge(_client.BIRTHDAY));
      // 合致する年代を選択
      setConstruction({ ...construction, AGE_CATEGORY: getAgeCategory(_client.BIRTHDAY) });
      // エラーのクリア
      setErrorFileds([...errorFileds].filter((x) => x != 'AGE_CATEGORY'));

      setIsBirthday(false);
    } else {
      // 無効な日付の場合、アラートを表示
      _client = { ..._client, BIRTHDAY: null };
      setIsBirthday(true);
    }
    setClient(_client);
  };

  // 年齢フォーカスアウト時の処理
  const handleAgeBlur = () => {
    if (age == null || age === '') return;

    const birthYear = getBirthYearFromAge(age, client.BIRTHDAY_MONTH, client.BIRTHDAY_DAY);
    const formattedBirthday = `${birthYear}/${String(client.BIRTHDAY_MONTH).padStart(
      2,
      '0'
    )}/${String(client.BIRTHDAY_DAY).padStart(2, '0')}`;

    const updated = { ...client, BIRTHDAY_YEAR: birthYear };
    setConstruction({ ...construction, AGE_CATEGORY: getAgeCategory(formattedBirthday) });
    setClient(updated);
    // エラーがある場合はクリアをする
    setErrorFileds([...errorFileds].filter((x) => x != 'BIRTHDAY_YEAR'));
    setErrorFileds([...errorFileds].filter((x) => x != 'AGE_CATEGORY'));
    setIsBirthday(false);
  };
  // 保存ボタン
  const handleOnClickSave = () => {
    var error = validate();
    if (error.length > 0) {
      setDialog({
        type: 'error',
        message: error,
      });
      return;
    }
    if (isSimilar) {
      setSimilarModalParam({
        client: similarClient,
        isSave: true,
        clientType: client.CLIENT_TYPE,
        okCallBack: () => {
          handleOnClickUpdate();
        },
      });
    } else {
      handleOnClickUpdate();
    }
  };

  const handleOnClickUpdate = () => {
    var clientData = {
      ...client,
      BIRTHDAY:
        client.CLIENT_TYPE == ClientType.USER
          ? `${client.BIRTHDAY_YEAR}-${String(client.BIRTHDAY_MONTH).padStart(2, '0')}-${String(
              client.BIRTHDAY_DAY
            ).padStart(2, '0')}` // 生年月日は YYYY-MM-DD 形式で保存
          : null, // 法人・公共はnull
      construction: mode == 'add' && isKoujiInput ? construction : null,
    };

    restApi.post(`/api/client/${mode}`, clientData, (data) => {
      // 呼び出し元に戻る
      navigate('/admin/clientmanage', {
        state: {
          targetClientCd: data['CLIENT_CD'],
          searchCondition: location?.state?.searchCondition,
        },
      });
    });
  };

  // 類似顧客情報モーダルを表示
  const handleOnSimilarAlertClick = () => {
    setSimilarModalParam({
      client: similarClient,
      isSave: false,
      clientType: client.CLIENT_TYPE,
    });
  };

  // フォーカスアウト
  const handleBlur = () => {
    // 重複チェック
    checkDuplicate();
  };

  // 入力チェック
  const validate = () => {
    let errors = [];
    if (client.CLIENT_TYPE != ClientType.USER) {
      checkRequired(client.NAME_1, '施主名１', 'NAME_1', errors);
    } else {
      checkRequired(client.LAST_NAME_1, '施主名１(姓)', 'LAST_NAME_1', errors);
      checkRequired(client.FIRST_NAME_1, '施主名１(名)', 'FIRST_NAME_1', errors);
      checkRequired(client.DM_KBN, 'DM', 'DM_KBN', errors, true);
      checkRequired(client.POST_CODE, '郵便番号', 'POST_CODE', errors);
      checkRequired(client.PREF, '都道府県', 'PREF', errors);
      checkRequired(client.CITY, '市', 'CITY', errors);
      checkRequired(client.TOWN, '町村', 'TOWN', errors);
      checkRequired(client.AREA, '地域', 'AREA', errors);
      checkRequired(client.ADDRESS, '番地', 'ADDRESS', errors);
      checkRequired(client.BIRTHDAY_YEAR, '生年月日の年', 'BIRTHDAY_YEAR', errors);
      checkRequired(client.TEL1, '電話番号1', 'TEL1', errors);
    }
    if (isSeikyuInput_check()) {
      checkRequired(client.SEIKYU_NAME, '請求先名', 'SEIKYU_NAME', errors);
      checkRequired(client.TEL1, '電話番号1', 'TEL1', errors);
      checkRequired(client.SEIKYU_POST_CODE, '請求先 郵便番号', 'SEIKYU_POST_CODE', errors);
      checkRequired(client.SEIKYU_PREF, '請求先 都道府県', 'SEIKYU_PREF', errors);
      checkRequired(client.SEIKYU_CITY, '請求先 市', 'SEIKYU_CITY', errors);
      checkRequired(client.SEIKYU_TOWN, '請求先 町村', 'SEIKYU_TOWN', errors);
      checkRequired(client.SEIKYU_ADDRESS, '請求先 番地', 'SEIKYU_ADDRESS', errors);
    }

    // 工事情報のチェック
    if (mode == 'add' && isKoujiInput) {
      if (client.CLIENT_TYPE != ClientType.USER) {
        checkRequired(client.POST_CODE, '郵便番号', 'POST_CODE', errors);
        checkRequired(client.PREF, '都道府県', 'PREF', errors);
        checkRequired(client.CITY, '市', 'CITY', errors);
        checkRequired(client.TOWN, '町村', 'TOWN', errors);
        checkRequired(client.AREA, '地域', 'AREA', errors);
        checkRequired(client.ADDRESS, '番地', 'ADDRESS', errors);
      }
      checkRequired(construction.KOUJI_MEI, '工事名', 'KOUJI_MEI', errors);
      checkRequired(
        construction.CONSTRUCTION_STATUS,
        'ステータス',
        'CONSTRUCTION_STATUS',
        errors,
        true
      );
      if (isEditableLostOrderStatus()) {
        checkRequired(construction.LOST_ORDER_DATE, '失注日', 'LOST_ORDER_DATE', errors);
      }
      checkRequired(construction.UKE_DATE, '見積依頼日', 'UKE_DATE', errors);
      checkRequired(construction.UKE_TYPE, '受付方法', 'UKE_TYPE', errors);
      checkRequired(construction.PROJECT_TYPE, '区分①', 'PROJECT_TYPE', errors);
      checkRequired(construction.KOUJI_KBN1, '区分③', 'KOUJI_KBN1', errors);
      checkRequired(construction.KOUJI_KBN2, '区分④', 'KOUJI_KBN2', errors);
      checkRequired(construction.UKE_TAN_CD, '受付担当', 'UKE_TAN_CD', errors);
      checkRequired(construction.SALES_TAN_CD, '営業担当', 'SALES_TAN_CD', errors);
      checkRequired(construction.VISIT_REASON, '来店のきっかけ', 'VISIT_REASON', errors, true);
      if (client.CLIENT_TYPE == ClientType.USER) {
        checkRequired(construction.AGE_CATEGORY, '年代(登録時)', 'AGE_CATEGORY', errors, true);
      }
    }

    setErrorFileds(errors.flatMap((x) => x.fields));

    return errors.map((x) => x.msg);
  };

  const getLostOrderStatusId = () => {
    const status = master.constructionStatus.find((x) => x.name === '失注');
    return status ? status.id : null;
  };
  // 失注ステータスは編集可能
  const isEditableLostOrderStatus = () => {
    const lostOrderId = getLostOrderStatusId();
    if (lostOrderId == null) return false;
    return construction.CONSTRUCTION_STATUS == lostOrderId;
  };

  // 請求先情報を入力可能かチェック
  const isSeikyuInput_check = () => {
    // 顧客区分が個人ではない、または請求先情報を分ける場合True
    return client.CLIENT_TYPE != ClientType.USER || client.INPUT_SEIKYU_FLG == 1;
  };

  // 削除ボタン
  const handleOnClickDelete = () => {
    setDialog({
      type: 'confirm',
      message: '削除します。よろしいですか？',
      okCallBack: () => {
        restApi.post(`/api/client/del`, client, (_) => {
          // 呼び出し元に戻る
          navigate('/admin/clientmanage', {
            state: {
              targetClientCd: '',
              searchCondition: location?.state?.searchCondition,
            },
          });
        });
      },
    });
  };

  // 戻るボタン
  const handleOnClickReturn = () => {
    navigate('/admin/clientmanage', {
      state: {
        targetClientCd: client['CLIENT_CD'],
        searchCondition: location?.state?.searchCondition,
      },
    });
  };

  return (
    <>
      <div className="edit-container">
        <div className="edit-content">
          <Card className="p-2 mb-0">
            <Row>
              <Col className="pr-1" md="2">
                <label>顧客区分</label>
                <br />
                <label className="client-type-lable">
                  <input
                    type="radio"
                    className="mr-2"
                    name="radioClientType"
                    value={ClientType.USER}
                    onChange={handleOnChangeClientType}
                    checked={client.CLIENT_TYPE == ClientType.USER}
                  />
                  個人
                </label>
                <label className="ml-2 client-type-lable">
                  <input
                    type="radio"
                    className="mr-2"
                    name="radioClientType"
                    value={ClientType.CORP}
                    onChange={handleOnChangeClientType}
                    checked={client.CLIENT_TYPE == ClientType.CORP}
                  />
                  法人
                </label>
                <label className="ml-2 client-type-lable">
                  <input
                    type="radio"
                    className="mr-2"
                    name="radioClientType"
                    value={ClientType.PUBLIC}
                    onChange={handleOnChangeClientType}
                    checked={client.CLIENT_TYPE == ClientType.PUBLIC}
                  />
                  公共
                </label>
              </Col>
              <Col hidden={mode != 'add'}>
                <br />
                <label className="mr-2" style={{ fontSize: '14pt', color: 'black' }}>
                  <input
                    className="mr-2"
                    style={{ transform: 'scale(1.5)' }}
                    type="checkbox"
                    checked={isKoujiInput}
                    onChange={() => setIsKoujiInput(!isKoujiInput)}
                  ></input>
                  工事情報／受付情報をあわせて入力
                </label>
              </Col>
            </Row>
            <Row>
              <Col className="pr-1" md="2">
                <Form.Group>
                  <label>顧客ID</label>
                  <TextComponent
                    disabled="true"
                    value={client.CLIENT_CD}
                    type="text"
                  ></TextComponent>
                </Form.Group>
              </Col>
              <Col className="pr-1" md="2">
                <label>初回問合せ日</label>
                <TextComponent
                  type="date"
                  value={client?.FIRST_INQUIRY_DAY}
                  onChange={handleChange('FIRST_INQUIRY_DAY')}
                ></TextComponent>
              </Col>
            </Row>
            <Row className="pt-1">
              {client.CLIENT_TYPE == ClientType.USER && (
                <>
                  <Col className="pr-1" md="2">
                    <Form.Group>
                      <label>
                        施主名１(姓)
                        <span class="badge badge-danger">必須</span>
                      </label>
                      <TextComponent
                        type="text"
                        value={client.LAST_NAME_1}
                        onChange={handleChange('LAST_NAME_1')}
                        onBlur={handleBlur}
                        error={errorFileds.some((x) => x == 'LAST_NAME_1')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>

                  <Col className="pr-1" md="2">
                    <Form.Group>
                      <label>
                        施主名１(名)
                        <span class="badge badge-danger">必須</span>
                      </label>
                      <TextComponent
                        type="text"
                        value={client.FIRST_NAME_1}
                        onChange={handleChange('FIRST_NAME_1')}
                        error={errorFileds.some((x) => x == 'FIRST_NAME_1')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                </>
              )}
              {client.CLIENT_TYPE != ClientType.USER && (
                <Col className="pr-1" md="3">
                  <Form.Group>
                    <label>
                      {client.CLIENT_TYPE == ClientType.CORP ? '法人名' : '公共名'}

                      <span class="badge badge-danger">必須</span>
                    </label>
                    <TextComponent
                      type="text"
                      value={client.NAME_1}
                      onChange={handleChange('NAME_1')}
                      error={errorFileds.some((x) => x == 'NAME_1')}
                    ></TextComponent>
                  </Form.Group>
                </Col>
              )}
              <Col className="pr-1" md="3">
                <Form.Group>
                  <label>
                    {client.CLIENT_TYPE == ClientType.USER
                      ? '施主名１カナ'
                      : client.CLIENT_TYPE == ClientType.CORP
                      ? '法人名カナ'
                      : '公共名カナ'}
                  </label>
                  <TextComponent
                    type="text"
                    value={client.NAME_KANA_1}
                    onChange={handleChange('NAME_KANA_1')}
                    onBlur={handleBlur}
                  ></TextComponent>
                </Form.Group>
              </Col>
            </Row>
            {client.CLIENT_TYPE == ClientType.USER && isSimilar && (
              <Row className="pt-1">
                <Col className="pr-1" md="3">
                  <label className="text-warning similar-alert" onClick={handleOnSimilarAlertClick}>
                    <WarningAmberIcon />
                    類似している顧客情報が既に登録されています
                  </label>
                </Col>
              </Row>
            )}
            {client.CLIENT_TYPE == ClientType.USER && (
              <>
                <Row className="pt-1">
                  <Col className="pr-1" md="2">
                    <Form.Group>
                      <label>施主名２(姓)</label>
                      <TextComponent
                        type="text"
                        value={client.LAST_NAME_2}
                        onChange={handleChange('LAST_NAME_2')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>

                  <Col className="pr-1" md="2">
                    <Form.Group>
                      <label>施主名２(名)</label>
                      <TextComponent
                        type="text"
                        value={client.FIRST_NAME_2}
                        onChange={handleChange('FIRST_NAME_2')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="3">
                    <Form.Group>
                      <label>施主名２カナ</label>
                      <TextComponent
                        type="text"
                        value={client.NAME_KANA_2}
                        onChange={handleChange('NAME_KANA_2')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="pt-1">
                  <Col className="pr-1 form-inline" md="3">
                    <label>
                      郵便番号<span class="badge badge-danger ml-1">必須</span>
                    </label>
                    <Form.Group>
                      <TextComponent
                        type="zipcode"
                        value={client.POST_CODE}
                        onChange={handleChange('POST_CODE')}
                        onBlur={handleBlur}
                        error={errorFileds.some((x) => x == 'POST_CODE')}
                      ></TextComponent>
                      <Button variant="default" onClick={() => searchZip('')}>
                        郵便番号検索
                      </Button>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="1">
                    <Form.Group>
                      <label>
                        都道府県<span class="badge badge-danger ml-1">必須</span>
                      </label>
                      <TextComponent
                        type="text"
                        value={client.PREF}
                        onChange={handleChange('PREF')}
                        error={errorFileds.some((x) => x == 'PREF')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="2">
                    <Form.Group>
                      <label>
                        市<span class="badge badge-danger ml-1">必須</span>
                      </label>
                      <TextComponent
                        type="text"
                        value={client.CITY}
                        onChange={handleChange('CITY')}
                        error={errorFileds.some((x) => x == 'CITY')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="2">
                    <Form.Group>
                      <label>
                        町村<span class="badge badge-danger ml-1">必須</span>
                      </label>
                      <TextComponent
                        type="text"
                        value={client.TOWN}
                        onChange={handleChange('TOWN')}
                        error={errorFileds.some((x) => x == 'TOWN')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="2">
                    <label>
                      地域<span class="badge badge-danger ml-1">必須</span>
                    </label>
                    <SelectComponent
                      value={client?.AREA}
                      onChange={handleChange('AREA')}
                      error={errorFileds.some((x) => x == 'AREA')}
                    >
                      {Areas.map((x, i) => (
                        <option key={i} value={x.key}>
                          {x.value}
                        </option>
                      ))}
                    </SelectComponent>
                  </Col>
                </Row>
                {isSimilar && (
                  <Row className="pt-1">
                    <Col className="pr-1" md="3">
                      <label
                        className="text-warning similar-alert"
                        onClick={handleOnSimilarAlertClick}
                      >
                        <WarningAmberIcon />
                        類似している顧客情報が既に登録されています
                      </label>
                    </Col>
                  </Row>
                )}
                <Row>
                  <Col className="pr-1" md="5">
                    <Form.Group>
                      <label>
                        番地<span class="badge badge-danger ml-1">必須</span>
                      </label>
                      <TextComponent
                        type="text"
                        value={client.ADDRESS}
                        onChange={handleChange('ADDRESS')}
                        error={errorFileds.some((x) => x == 'ADDRESS')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="6">
                    <Form.Group>
                      <label>建物名</label>
                      <TextComponent
                        type="text"
                        value={client.BUILDING}
                        onChange={handleChange('BUILDING')}
                        error={errorFileds.some((x) => x == 'BUILDING')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
            <Row className="pt-1">
              {client.CLIENT_TYPE == ClientType.USER && (
                <>
                  <Col className="pr-1" md="3">
                    <Form.Group>
                      <label>
                        電話番号１<span class="badge badge-danger ml-1">必須</span>
                      </label>
                      <TextComponent
                        type="tel"
                        value={client.TEL1}
                        onChange={handleChange('TEL1')}
                        error={errorFileds.some((x) => x == 'TEL1')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="2">
                    <Form.Group>
                      <label>続柄1</label>
                      <AutoCompleteComponent
                        displayMemberPath="value"
                        itemsSource={TelRelation}
                        value={client?.TEL_RELATION_1}
                        onChange={(inputElement, target) => {
                          handleChange('TEL_RELATION_1')(target);
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="3">
                    <Form.Group>
                      <label>電話番号２</label>
                      <TextComponent
                        type="tel"
                        value={client.TEL2}
                        onChange={handleChange('TEL2')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="2">
                    <Form.Group>
                      <label>続柄2</label>
                      <AutoCompleteComponent
                        value={client?.TEL_RELATION_2}
                        displayMemberPath="value"
                        itemsSource={TelRelation}
                        onChange={(inputElement, target) => {
                          handleChange('TEL_RELATION_2')(target);
                        }}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
              <Col className="pr-1" md="5">
                <Form.Group>
                  <label>メールアドレス</label>
                  <TextComponent
                    type="email"
                    value={client.MAIL}
                    onChange={handleChange('MAIL')}
                    error={errorFileds.some((x) => x == 'MAIL')}
                  ></TextComponent>
                </Form.Group>
              </Col>
              <Col className="pr-1" md="6">
                <Form.Group>
                  <label>備考</label>
                  <TextComponent
                    type="text"
                    value={client.BIKO}
                    onChange={handleChange('BIKO')}
                  ></TextComponent>
                </Form.Group>
              </Col>
            </Row>
            {/* 個人の場合の項目 */}
            {client.CLIENT_TYPE == ClientType.USER && (
              <>
                <Row className="pt-1">
                  <Col className="pr-1" md="3">
                    <Form.Group>
                      <label>
                        生年月日<span class="badge badge-danger ml-1">必須</span>
                      </label>
                      <div className="d-flex">
                        <TextComponent
                          type="text"
                          value={client.BIRTHDAY_YEAR}
                          onChange={handleChange('BIRTHDAY_YEAR')}
                          maxLength="4"
                          onBlur={isValidBirthdate}
                          error={errorFileds.some((x) => x == 'BIRTHDAY_YEAR')}
                        ></TextComponent>
                        <label className="mt-2" style={{ fontSize: '14pt', color: 'black' }}>
                          年
                        </label>
                        <SelectComponent
                          value={client.BIRTHDAY_MONTH}
                          onChange={handleChange('BIRTHDAY_MONTH')}
                          onBlur={isValidBirthdate}
                          className="form-control ml-2"
                        >
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                            <option key={month} value={month}>
                              {month}
                            </option>
                          ))}
                        </SelectComponent>
                        <label className="mt-2" style={{ fontSize: '14pt', color: 'black' }}>
                          月
                        </label>
                        <SelectComponent
                          value={client.BIRTHDAY_DAY}
                          onChange={handleChange('BIRTHDAY_DAY')}
                          onBlur={isValidBirthdate}
                          className="form-control ml-2"
                        >
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </SelectComponent>
                        <label className="mt-2" style={{ fontSize: '14pt', color: 'black' }}>
                          日
                        </label>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="1">
                    <Form.Group>
                      <label>年齢(現在)</label>
                      <div className="d-flex">
                        <TextComponent
                          value={age}
                          onBlur={handleAgeBlur}
                          onChange={handleChange('AGE')}
                          maxLength="3"
                          type="text"
                        ></TextComponent>
                        <label className="mt-2" style={{ fontSize: '14pt', color: 'black' }}>
                          歳
                        </label>
                      </div>
                    </Form.Group>
                  </Col>
                  {mode == 'add' && isKoujiInput && (
                    <Col className="pr-1" md="2">
                      <label>
                        年代(登録時)<span class="badge badge-danger ml-1">必須</span>
                      </label>
                      <SelectComponent
                        value={construction?.AGE_CATEGORY}
                        onChange={handleOnChangeConstruction('AGE_CATEGORY')}
                        error={errorFileds.some((x) => x == 'AGE_CATEGORY')}
                      >
                        {AgeCategories.map((x, i) => (
                          <option key={i} value={x.key}>
                            {x.value}
                          </option>
                        ))}
                      </SelectComponent>
                    </Col>
                  )}
                </Row>
                {/* 個人の場合で生年月日の整合性エラー */}
                {isBirthday && (
                  <Row className="pt-1">
                    <Col className="pr-1" md="2">
                      <label className="text-warning">
                        <WarningAmberIcon />
                        この生年月日は存在しません
                      </label>
                    </Col>
                  </Row>
                )}
                <Row className="pt-3">
                  <Col className="pr-1" md="4">
                    <label className="mr-1" style={{ fontSize: '14pt', color: 'black' }}>
                      <input
                        className="mr-1"
                        type="checkbox"
                        checked={client.INPUT_SEIKYU_FLG == 1}
                        onChange={handleChange('INPUT_SEIKYU_FLG')}
                      ></input>
                      請求先情報を分ける
                    </label>
                  </Col>
                </Row>
              </>
            )}
            {isSeikyuInput_check() && (
              <>
                <Row className="pt-2">
                  <Col className="pr-1" md="4">
                    <b>＜請求先情報＞</b>
                  </Col>
                </Row>
                <Row className="pt-1">
                  <Col className="pr-1" md="3">
                    <Form.Group>
                      <label>
                        請求先名
                        <span class="badge badge-danger">必須</span>
                      </label>
                      <TextComponent
                        type="text"
                        value={client.SEIKYU_NAME}
                        onChange={handleChange('SEIKYU_NAME')}
                        onBlur={handleBlur}
                        error={errorFileds.some((x) => x == 'SEIKYU_NAME')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                </Row>
                {client.CLIENT_TYPE != ClientType.USER && isSimilar && (
                  <Row className="pt-1">
                    <Col className="pr-1" md="3">
                      <label
                        className="text-warning similar-alert"
                        onClick={handleOnSimilarAlertClick}
                      >
                        <WarningAmberIcon />
                        類似している顧客情報が既に登録されています
                      </label>
                    </Col>
                  </Row>
                )}
                {client.CLIENT_TYPE != ClientType.USER && mode == 'add' && isKoujiInput && (
                  <>
                    <Row className="pt-1">
                      <Col className="pr-1" md="3">
                        <Form.Group>
                          <label>部署名</label>
                          <TextComponent
                            type="text"
                            value={construction.DEPARTMENT}
                            onChange={handleOnChangeConstruction('DEPARTMENT')}
                          ></TextComponent>
                        </Form.Group>
                      </Col>
                      <Col className="pr-1" md="3">
                        <Form.Group>
                          <label>担当者</label>
                          <TextComponent
                            type="text"
                            value={construction.REPRESENTATIVE}
                            onChange={handleOnChangeConstruction('REPRESENTATIVE')}
                          ></TextComponent>
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
                {client.CLIENT_TYPE != ClientType.USER && (
                  <Row className="pt-1">
                    <Col className="pr-1" md="3">
                      <Form.Group>
                        <label>
                          電話番号１<span class="badge badge-danger ml-1">必須</span>
                        </label>
                        <TextComponent
                          type="tel"
                          value={client.TEL1}
                          onChange={handleChange('TEL1')}
                          error={errorFileds.some((x) => x == 'TEL1')}
                        ></TextComponent>
                      </Form.Group>
                    </Col>
                    <Col className="pr-1" md="3">
                      <Form.Group>
                        <label>電話番号２</label>
                        <TextComponent
                          type="tel"
                          value={client.TEL2}
                          onChange={handleChange('TEL2')}
                        ></TextComponent>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                <Row className="pt-1">
                  <Col className="pr-1 form-inline" md="3">
                    <label>
                      郵便番号<span class="badge badge-danger ml-1">必須</span>
                    </label>
                    <Form.Group>
                      <TextComponent
                        type="zipcode"
                        value={client.SEIKYU_POST_CODE}
                        onChange={handleChange('SEIKYU_POST_CODE')}
                        error={errorFileds.some((x) => x == 'SEIKYU_POST_CODE')}
                      ></TextComponent>
                      <Button variant="default" onClick={() => searchZip('SEIKYU_')}>
                        郵便番号検索
                      </Button>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="1">
                    <Form.Group>
                      <label>
                        都道府県<span class="badge badge-danger ml-1">必須</span>
                      </label>
                      <TextComponent
                        type="text"
                        value={client.SEIKYU_PREF}
                        onChange={handleChange('SEIKYU_PREF')}
                        error={errorFileds.some((x) => x == 'SEIKYU_PREF')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="2">
                    <Form.Group>
                      <label>
                        市<span class="badge badge-danger ml-1">必須</span>
                      </label>
                      <TextComponent
                        type="text"
                        value={client.SEIKYU_CITY}
                        onChange={handleChange('SEIKYU_CITY')}
                        error={errorFileds.some((x) => x == 'SEIKYU_CITY')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="2">
                    <Form.Group>
                      <label>
                        町村<span class="badge badge-danger ml-1">必須</span>
                      </label>
                      <TextComponent
                        type="text"
                        value={client.SEIKYU_TOWN}
                        onChange={handleChange('SEIKYU_TOWN')}
                        error={errorFileds.some((x) => x == 'SEIKYU_TOWN')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="5">
                    <Form.Group>
                      <label>
                        番地<span class="badge badge-danger ml-1">必須</span>
                      </label>
                      <TextComponent
                        type="text"
                        value={client.SEIKYU_ADDRESS}
                        onChange={handleChange('SEIKYU_ADDRESS')}
                        error={errorFileds.some((x) => x == 'SEIKYU_ADDRESS')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="6">
                    <Form.Group>
                      <label>建物名</label>
                      <TextComponent
                        type="text"
                        value={client.SEIKYU_BUILDING}
                        onChange={handleChange('SEIKYU_BUILDING')}
                        error={errorFileds.some((x) => x == 'SEIKYU_BUILDING')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
            {client.CLIENT_TYPE == ClientType.USER && (
              <Row className="pt-1">
                <Col className="pr-1" md="2">
                  <label>
                    DM<span class="badge badge-danger ml-1">必須</span>
                  </label>
                  <SelectComponent
                    value={client?.DM_KBN}
                    onChange={handleChange('DM_KBN')}
                    error={errorFileds.some((x) => x == 'DM_KBN')}
                  >
                    {DmKbn.map((x, i) => (
                      <option key={i} value={x.key}>
                        {x.value}
                      </option>
                    ))}
                  </SelectComponent>
                </Col>
              </Row>
            )}
            {mode == 'add' && isKoujiInput && (
              <>
                <Row className="pt-2">
                  <Col className="pr-1" md="4">
                    <b>＜工事情報＞</b>
                  </Col>
                </Row>
                <Row>
                  <Col className="pl-3" md="4">
                    <Form.Group>
                      <label>
                        工事名（例：〇〇様　工事名）
                        <span class="badge badge-danger ml-1">必須</span>
                      </label>
                      <br />
                      <TextComponent
                        type="text"
                        placeholder="〇〇様　工事名"
                        value={construction?.KOUJI_MEI}
                        onChange={handleOnChangeConstruction('KOUJI_MEI')}
                        error={errorFileds.some((x) => x == 'KOUJI_MEI')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="2">
                    <label>打ち合わせ予定日</label>
                    <TextComponent
                      type="date"
                      value={construction?.MEETING_SCHEDULED_DATE}
                      onChange={handleOnChangeConstruction('MEETING_SCHEDULED_DATE')}
                    ></TextComponent>
                  </Col>
                </Row>
                <Row>
                  <Col className="pl-3" md="4">
                    <Form.Group>
                      <label>依頼内容</label>
                      <br />
                      <TextComponent
                        type="text"
                        placeholder="造園工事など"
                        value={construction?.IRAI_MEI}
                        onChange={handleOnChangeConstruction('IRAI_MEI')}
                        error={errorFileds.some((x) => x == 'IRAI_MEI')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="2">
                    <label>ロボット機(工事種類)</label>
                    <SelectComponent
                      value={construction?.ROBOT_TYPE}
                      onChange={handleOnChangeConstruction('ROBOT_TYPE')}
                      error={errorFileds.some((x) => x == 'ROBOT_TYPE')}
                    >
                      <option value={'0'}></option>
                      {master.robotTypes.map((x, i) => (
                        <option key={i} value={x['ROBOT_TYPE']}>
                          {x['ROBOT_TYPE_NAME']}
                        </option>
                      ))}
                    </SelectComponent>
                  </Col>
                  <Col className="pl-3" md="4">
                    <Form.Group>
                      <label>ロボット機種名</label>
                      <br />
                      <TextComponent
                        type="text"
                        placeholder="ロボット機種名（複数ある場合は、カンマ区切りで入力）"
                        value={construction?.ROBOT_MAKER}
                        onChange={handleOnChangeConstruction('ROBOT_MAKER')}
                        error={errorFileds.some((x) => x == 'ROBOT_MAKER')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col className="pr-1" md="2">
                    <label>
                      ステータス<span class="badge badge-danger ml-1">必須</span>
                    </label>
                    <SelectComponent
                      value={construction?.CONSTRUCTION_STATUS}
                      onChange={handleOnChangeConstruction('CONSTRUCTION_STATUS')}
                      error={errorFileds.some((x) => x == 'CONSTRUCTION_STATUS')}
                    >
                      {master.constructionStatus.map((x, i) => (
                        <option key={i} value={x['id']}>
                          {x['name']}
                        </option>
                      ))}
                    </SelectComponent>
                  </Col>
                  {isEditableLostOrderStatus() && (
                    <Col className="pr-1" md="2">
                      <label>
                        失注日<span class="badge badge-danger ml-1">必須</span>
                      </label>
                      <TextComponent
                        type="date"
                        value={construction?.LOST_ORDER_DATE}
                        onChange={handleOnChangeConstruction('LOST_ORDER_DATE')}
                        error={errorFileds.some((x) => x == 'LOST_ORDER_DATE')}
                      ></TextComponent>
                    </Col>
                  )}
                </Row>
                <Row>
                  <Col className="pl-3 pt-3" md="10">
                    <b>＜受付情報＞</b>
                  </Col>
                </Row>
                <Row>
                  <Col className="pr-1" md="2">
                    <label>
                      見積依頼日<span class="badge badge-danger ml-1">必須</span>
                    </label>
                    <TextComponent
                      type="date"
                      value={construction?.UKE_DATE}
                      onChange={handleOnChangeConstruction('UKE_DATE')}
                      error={errorFileds.some((x) => x == 'UKE_DATE')}
                    ></TextComponent>
                  </Col>
                  <Col className="pr-1" md="2">
                    <label>
                      受付方法<span class="badge badge-danger ml-1">必須</span>
                    </label>
                    <SelectComponent
                      value={construction?.UKE_TYPE}
                      onChange={handleOnChangeConstruction('UKE_TYPE')}
                      error={errorFileds.some((x) => x == 'UKE_TYPE')}
                    >
                      <option value={'　'}></option>
                      {master.ukeType.map((x, i) => (
                        <option key={i} value={x['id']}>
                          {x['name']}
                        </option>
                      ))}
                    </SelectComponent>
                  </Col>
                  <Col className="pr-1" md="4">
                    <label>受付方法備考</label>
                    <TextComponent
                      type="text"
                      value={construction?.UKE_TYPE_BIKO}
                      onChange={handleOnChangeConstruction('UKE_TYPE_BIKO')}
                    ></TextComponent>
                  </Col>
                </Row>
                <Row>
                  <Col className="pr-1" md="2">
                    <label>
                      区分①<span class="badge badge-danger ml-1">必須</span>
                    </label>
                    <SelectComponent
                      value={construction?.PROJECT_TYPE}
                      onChange={handleOnChangeConstruction('PROJECT_TYPE')}
                      error={errorFileds.some((x) => x == 'PROJECT_TYPE')}
                    >
                      {ProjectTypes.map((x, i) => (
                        <option key={i} value={x.key}>
                          {x.value}
                        </option>
                      ))}
                    </SelectComponent>
                  </Col>
                  <Col className="pr-1" md="2">
                    <Form.Group>
                      <label>区分②</label>
                      <TextComponent
                        value={
                          client.CLIENT_TYPE == ClientType.USER
                            ? '個人'
                            : client.CLIENT_TYPE == ClientType.CORP
                            ? '法人'
                            : '公共'
                        }
                        type="text"
                        disabled={true}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-1" md="2">
                    <label>
                      区分③<span class="badge badge-danger ml-1">必須</span>
                    </label>
                    <SelectComponent
                      value={construction?.KOUJI_KBN1}
                      onChange={handleOnChangeConstruction('KOUJI_KBN1')}
                      error={errorFileds.some((x) => x == 'KOUJI_KBN1')}
                    >
                      {KoujiKbn1.map((x, i) => (
                        <option key={i} value={x.key}>
                          {x.value}
                        </option>
                      ))}
                    </SelectComponent>
                  </Col>
                  <Col className="pr-1" md="2">
                    <label>
                      区分④<span class="badge badge-danger ml-1">必須</span>
                    </label>
                    <SelectComponent
                      value={construction?.KOUJI_KBN2}
                      onChange={handleOnChangeConstruction('KOUJI_KBN2')}
                      error={errorFileds.some((x) => x == 'KOUJI_KBN2')}
                    >
                      {KoujiKbn2.filter((x) =>
                        x.KoujiKbn1.includes(parseInt(construction?.KOUJI_KBN1))
                      ).map((x, i) => (
                        <option key={i} value={x.key}>
                          {x.value}
                        </option>
                      ))}
                    </SelectComponent>
                  </Col>
                </Row>
                <Row>
                  <Col className="pr-1" md="2">
                    <label>
                      受付担当<span class="badge badge-danger ml-1">必須</span>
                    </label>
                    <SelectComponent
                      value={construction?.UKE_TAN_CD}
                      onChange={handleOnChangeConstruction('UKE_TAN_CD')}
                      error={errorFileds.some((x) => x == 'UKE_TAN_CD')}
                    >
                      <option value={'　'}></option>
                      {master.planer.map((x, i) => (
                        <option key={i} value={x['id']}>
                          {x['name']}
                        </option>
                      ))}
                    </SelectComponent>
                  </Col>
                  <Col className="pr-1" md="2">
                    <label>
                      営業担当<span class="badge badge-danger ml-1">必須</span>
                    </label>
                    <SelectComponent
                      value={construction?.SALES_TAN_CD}
                      onChange={handleOnChangeConstruction('SALES_TAN_CD')}
                      error={errorFileds.some((x) => x == 'SALES_TAN_CD')}
                    >
                      <option value={'　'}></option>
                      {master.salesStaff.map((x, i) => (
                        <option key={i} value={x['id']}>
                          {x['name']}
                        </option>
                      ))}
                    </SelectComponent>
                  </Col>
                  <Col className="pr-1" md="2">
                    <label>現場担当</label>
                    <SelectComponent
                      value={construction?.GENBA_TAN_CD}
                      onChange={handleOnChangeConstruction('GENBA_TAN_CD')}
                    >
                      <option value={0}></option>
                      {master.genbaStaff.map((x, i) => (
                        <option key={i} value={x['id']}>
                          {x['name']}
                        </option>
                      ))}
                    </SelectComponent>
                  </Col>
                </Row>
                <Row>
                  <Col className="pr-1" md="4">
                    <label>
                      来店のきっかけ<span class="badge badge-danger ml-1">必須</span>
                    </label>
                    <SelectComponent
                      value={construction?.VISIT_REASON}
                      onChange={handleOnChangeConstruction('VISIT_REASON')}
                      error={errorFileds.some((x) => x == 'VISIT_REASON')}
                    >
                      <option value={0}></option>
                      {VisitReason.filter(
                        (x) => x.type == parseInt(construction?.PROJECT_TYPE)
                      ).map((x, i) => (
                        <option key={i} value={x.key}>
                          {x.value}
                        </option>
                      ))}
                    </SelectComponent>
                  </Col>
                  <Col className="pr-1" md="4">
                    <label>来店のきっかけ備考</label>
                    <TextComponent
                      type="text"
                      value={construction?.VISIT_REASON_BIKO}
                      onChange={handleOnChangeConstruction('VISIT_REASON_BIKO')}
                    ></TextComponent>
                  </Col>
                </Row>
                {/* 法人・公共の場合の項目 */}
                {client.CLIENT_TYPE != ClientType.USER && (
                  <>
                    <Row className="pt-2">
                      <Col className="pr-1" md="2">
                        <b>＜工事場所＞</b>
                      </Col>
                    </Row>

                    <Row className="pt-1">
                      <Col className="pr-1 form-inline" md="3">
                        <label>
                          郵便番号<span class="badge badge-danger ml-1">必須</span>
                        </label>
                        <Form.Group>
                          <TextComponent
                            type="zipcode"
                            value={client.POST_CODE}
                            onChange={handleChange('POST_CODE')}
                            error={errorFileds.some((x) => x == 'POST_CODE')}
                          ></TextComponent>
                          <Button variant="default" onClick={() => searchZip('')}>
                            郵便番号検索
                          </Button>
                        </Form.Group>
                      </Col>
                      <Col className="pr-1" md="1">
                        <Form.Group>
                          <label>
                            都道府県<span class="badge badge-danger ml-1">必須</span>
                          </label>
                          <TextComponent
                            type="text"
                            value={client.PREF}
                            onChange={handleChange('PREF')}
                            error={errorFileds.some((x) => x == 'PREF')}
                          ></TextComponent>
                        </Form.Group>
                      </Col>
                      <Col className="pr-1" md="2">
                        <Form.Group>
                          <label>
                            市<span class="badge badge-danger ml-1">必須</span>
                          </label>
                          <TextComponent
                            type="text"
                            value={client.CITY}
                            onChange={handleChange('CITY')}
                            error={errorFileds.some((x) => x == 'CITY')}
                          ></TextComponent>
                        </Form.Group>
                      </Col>
                      <Col className="pr-1" md="2">
                        <Form.Group>
                          <label>
                            町村<span class="badge badge-danger ml-1">必須</span>
                          </label>
                          <TextComponent
                            type="text"
                            value={client.TOWN}
                            onChange={handleChange('TOWN')}
                            error={errorFileds.some((x) => x == 'TOWN')}
                          ></TextComponent>
                        </Form.Group>
                      </Col>
                      <Col className="pr-1" md="2">
                        <label>
                          地域<span class="badge badge-danger ml-1">必須</span>
                        </label>
                        <SelectComponent
                          value={client?.AREA}
                          onChange={handleChange('AREA')}
                          error={errorFileds.some((x) => x == 'AREA')}
                        >
                          {Areas.map((x, i) => (
                            <option key={i} value={x.key}>
                              {x.value}
                            </option>
                          ))}
                        </SelectComponent>
                      </Col>
                    </Row>
                    <Row>
                      <Col className="pr-1" md="5">
                        <Form.Group>
                          <label>
                            番地<span class="badge badge-danger ml-1">必須</span>
                          </label>
                          <TextComponent
                            type="text"
                            value={client.ADDRESS}
                            onChange={handleChange('ADDRESS')}
                            error={errorFileds.some((x) => x == 'ADDRESS')}
                          ></TextComponent>
                        </Form.Group>
                      </Col>
                      <Col className="pr-1" md="6">
                        <Form.Group>
                          <label>建物名</label>
                          <TextComponent
                            type="text"
                            value={client.BUILDING}
                            onChange={handleChange('BUILDING')}
                            error={errorFileds.some((x) => x == 'BUILDING')}
                          ></TextComponent>
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
              </>
            )}
          </Card>
        </div>
        <div className="edit-footer p-2">
          <Button variant="secondary" className="footer-button" onClick={handleOnClickReturn}>
            戻る
          </Button>
          {mode == 'del' ? (
            <Button
              variant="danger"
              className="btn-fill footer-button"
              onClick={handleOnClickDelete}
            >
              削除
            </Button>
          ) : (
            <Button
              variant="primary"
              className="btn-fill footer-button"
              onClick={handleOnClickSave}
            >
              保存
            </Button>
          )}
        </div>
      </div>
      <MessageModal dialog={dialog}></MessageModal>
      {/* 類似顧客確認モーダル */}
      <SimilarModal params={similarModalParam}></SimilarModal>
    </>
  );
};
export default ClientEdit;
